import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';

const GUIDES = {
  user: { file: `${process.env.PUBLIC_URL}/USER_GUIDE.md`, label: '👤 User Guide', github: 'https://github.com/Advanced-Geosciences-Inc/ultimate-gis/blob/main/USER_GUIDE.md' },
  builder: { file: `${process.env.PUBLIC_URL}/BUILDER_GUIDE.md`, label: '🛠 Builder Guide', github: 'https://github.com/Advanced-Geosciences-Inc/ultimate-gis/blob/main/BUILDER_GUIDE.md' },
};

/**
 * Help — renders USER_GUIDE.md and BUILDER_GUIDE.md (served from /public)
 * inside the app, tabbed for the two audiences. GitHub-style anchor links
 * work via rehype-slug.
 */
function Help() {
  const { theme, C } = useTheme();
  const [tab, setTab] = useState('user');
  const [markdown, setMarkdown] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    setMarkdown(null);
    setError(null);
    fetch(GUIDES[tab].file)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(setMarkdown)
      .catch(err => setError(err.message));
  }, [tab]);

  // Section filter: keep the preamble + any "## " section whose text matches
  const shown = React.useMemo(() => {
    if (!markdown || !filter.trim()) return markdown;
    const q = filter.toLowerCase();
    const parts = markdown.split(/\n(?=## )/);
    const kept = parts.filter((p, i) => i === 0 || p.toLowerCase().includes(q));
    return kept.length > 1 ? kept.join('\n') : `## No sections match “${filter}”\n\nTry a different keyword — or clear the search.`;
  }, [markdown, filter]);

  return (
    <Layout>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px 80px' }}>
        {/* Header + tabs + search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text0, margin: 0 }}>📖 Help</h1>
          {/* Audience tabs */}
          <div style={{ display: 'flex', gap: 4, background: C.bg2, borderRadius: 8, padding: 3, border: `1px solid ${C.border}` }}>
            {Object.entries(GUIDES).map(([id, g]) => (
              <button key={id} onClick={() => { setTab(id); setFilter(''); }}
                style={{ fontSize: 12, padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: tab === id ? 600 : 400, background: tab === id ? C.bg4 : 'transparent', color: tab === id ? C.accent : C.text2 }}>
                {g.label}
              </button>
            ))}
          </div>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder={tab === 'user' ? 'Filter sections… (e.g. kriging, import, inversion)' : 'Filter sections… (e.g. deploy, migrations, pitfalls)'}
            style={{ marginLeft: 'auto', width: 260, fontSize: 12, padding: '7px 12px', borderRadius: 8, background: C.bg3, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}
          />
          <a href={GUIDES[tab].github}
            target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 11, color: C.accent, textDecoration: 'none' }}>
            View on GitHub ↗
          </a>
        </div>
        <div style={{ fontSize: 11, color: C.text3, marginBottom: 20 }}>
          Tip: the section filter above narrows by keyword; use Ctrl+F for exact text within the page.
        </div>

        {error && (
          <div style={{ padding: 14, borderRadius: 8, background: 'rgba(248,81,73,0.1)', border: '1px solid rgba(248,81,73,0.3)', color: '#f85149', fontSize: 13 }}>
            Could not load the guide ({error}). It's also available on
            {' '}<a href={GUIDES[tab].github} style={{ color: C.accent }}>GitHub</a>.
          </div>
        )}
        {!markdown && !error && (
          <div style={{ color: C.text3, fontSize: 13 }}>Loading guide…</div>
        )}

        {shown && (
          <div className="help-md">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSlug]}
              components={{
                a: ({ href, children }) => (
                  <a href={href} target={href?.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer" style={{ color: C.accent }}>
                    {children}
                  </a>
                ),
              }}
            >
              {shown}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Scoped markdown styling — dark navy theme */}
      <style>{`
        .help-md { color: ${C.text1}; font-size: 13px; line-height: 1.65; }
        .help-md h1 { font-size: 24px; color: ${C.text0}; border-bottom: 1px solid ${C.bg4}; padding-bottom: 8px; margin: 28px 0 14px; }
        .help-md h2 { font-size: 19px; color: ${C.text0}; border-bottom: 1px solid ${C.bg4}; padding-bottom: 6px; margin: 30px 0 12px; scroll-margin-top: 16px; }
        .help-md h3 { font-size: 15px; color: ${C.accent}; margin: 20px 0 8px; }
        .help-md p { margin: 8px 0; }
        .help-md em { color: ${C.text2}; }
        .help-md strong { color: ${C.text0}; }
        .help-md code { background: ${C.bg3}; padding: 1px 6px; border-radius: 4px; font-size: 12px; color: ${C.accent}; }
        .help-md pre { background: ${C.bg2}; border: 1px solid ${C.bg4}; border-radius: 8px; padding: 12px; overflow-x: auto; }
        .help-md pre code { background: none; color: ${C.text1}; padding: 0; }
        .help-md table { border-collapse: collapse; margin: 12px 0; width: 100%; display: block; overflow-x: auto; }
        .help-md th { background: ${C.bg2}; color: ${C.text0}; text-align: left; padding: 6px 12px; border: 1px solid ${C.bg4}; font-size: 12px; }
        .help-md td { padding: 6px 12px; border: 1px solid ${C.bg4}; font-size: 12px; }
        .help-md tr:nth-child(even) td { background: ${theme === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'}; }
        .help-md ul, .help-md ol { padding-left: 22px; margin: 8px 0; }
        .help-md li { margin: 3px 0; }
        .help-md blockquote { border-left: 3px solid ${C.accent}; margin: 10px 0; padding: 4px 14px; color: ${C.text2}; background: rgba(14,116,144,0.06); border-radius: 0 6px 6px 0; }
        .help-md hr { border: none; border-top: 1px solid ${C.bg4}; margin: 24px 0; }
      `}</style>
    </Layout>
  );
}

export default Help;
